/**
 * LocalClockWeather — compact running clock + wttr.in weather chip
 * anchored to the user's selected locality (falls back to IP geo).
 * Placed next to the "Welcome from ..." pill on the home page.
 */
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useLocality } from "@/contexts/LocalityContext";
import { useGeo } from "@/lib/useGeo";
import { Clock, CloudSun } from "lucide-react";

export default function LocalClockWeather() {
  const { locality } = useLocality();
  const { geo } = useGeo();

  const city = locality?.city || geo?.city;
  const country = locality?.country || geo?.country_name;
  const label = [city, country].filter(Boolean).join(", ") || "your location";

  // Running clock — one tick per second, formatted in the user's local
  // browser timezone (that's what "current time" means to them).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const timeStr = useMemo(
    () => now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    [now],
  );
  const dateStr = useMemo(
    () => now.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }),
    [now],
  );

  // Weather from the same community endpoint used by CommunityHub — served
  // from the 6h wttr.in cache we already populate.
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    if (!city) { setWeather(null); return; }
    let cancelled = false;
    // Build a location slug from the locality if present; else use city as a
    // free-form slug (backend accepts a city-only path).
    const slug = [country, locality?.state, city].filter(Boolean).map((s) =>
      String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
    ).join("/");
    http.get(`/community/${slug}`)
      .then((r) => { if (!cancelled) setWeather(r.data?.weather || null); })
      .catch(() => { if (!cancelled) setWeather(null); });
    return () => { cancelled = true; };
  }, [city, country, locality?.state]);

  return (
    <div
      data-testid="local-clock-weather"
      className="inline-flex items-center gap-3 rounded-full pl-3 pr-4 py-1.5 text-xs mt-2"
      style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #FEF3C7 100%)", border: "1px solid #E5E7EB" }}
      title={label}
    >
      <span className="inline-flex items-center gap-1.5" style={{ color: "#4338CA" }} data-testid="lcw-clock">
        <Clock className="w-3.5 h-3.5" />
        <span className="font-mono font-semibold">{timeStr}</span>
        <span className="opacity-70 hidden sm:inline">· {dateStr}</span>
      </span>
      {weather?.current && (
        <span className="inline-flex items-center gap-1.5 border-l pl-3" style={{ borderColor: "#E5E7EB", color: "#92400E" }} data-testid="lcw-weather">
          <CloudSun className="w-3.5 h-3.5" />
          <span className="font-semibold">{weather.current.temp_c}°C</span>
          <span className="opacity-80 hidden md:inline">{weather.current.condition}</span>
        </span>
      )}
    </div>
  );
}
